/**
 * Sign in (screen 1). The operator authenticates against the identity provider, not against this
 * application: there is no password field here, so this client never holds a credential it could leak.
 *
 * There is ONE sign-in: the identity provider's page, themed as this screen (ops/keycloak/themes/corpex-ebr).
 * So a visitor without a session is sent there straight away; this screen only shows while that happens
 * ("Taking you to sign in…") or when it fails, with a reason the operator can act on and a retry.
 */
import { useCallback, useEffect, useState } from "react";
import logoWhite from "../assets/corpex-logo.png";
import { setToken } from "../lib/api.js";
import { beginSignIn, completeSignIn, OIDC_ISSUER, takeReturn } from "../lib/auth.js";
import { sessionRefresher } from "../lib/session.js";

type Phase = "CHECKING" | "REDIRECTING" | "FAILED";

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [phase, setPhase] = useState<Phase>("CHECKING");
  const [problem, setProblem] = useState<string | null>(null);

  const start = useCallback(() => {
    setPhase("REDIRECTING");
    beginSignIn().catch((e: unknown) => {
      setProblem(
        e instanceof Error
          ? `${e.message} The identity provider may be starting up; wait a moment and try again.`
          : String(e),
      );
      setPhase("FAILED");
    });
  }, []);

  // A page load may be an ordinary visit or the provider sending the operator back with a code.
  useEffect(() => {
    let cancelled = false;
    completeSignIn()
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          start();
          return;
        }
        setToken(result.accessToken);
        sessionRefresher.start(result); // five-minute tokens are renewed, so no one is dropped mid-work
        const back = takeReturn(); // back to where the person was, not Home
        if (back) window.history.replaceState(null, "", back);
        onSignedIn();
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setProblem(e instanceof Error ? e.message : String(e));
        setPhase("FAILED");
      });
    return () => {
      cancelled = true;
    };
  }, [onSignedIn, start]);

  return (
    <main className="signin-ground">
      <div className="signin-card">
        <aside className="signin-stage">
          <img src={logoWhite} alt="Corpex" width={176} height={55} className="signin-logo" />
          <div className="signin-welcome">
            <p className="signin-headline">Welcome to EBR</p>
            <p className="signin-tagline">Electronic batch records</p>
          </div>
        </aside>

        <section className="signin-panel" aria-labelledby="signin-title">
          <h1 id="signin-title" className="signin-title">
            Sign in
          </h1>

          {phase === "CHECKING" ? <p className="signin-copy">Completing your sign-in…</p> : null}

          {phase === "FAILED" && problem ? (
            <p role="alert" className="signin-problem">
              {problem}
            </p>
          ) : null}

          {phase === "FAILED" ? (
            <button type="button" onClick={start} className="signin-button">
              Try again
            </button>
          ) : null}

          {phase === "REDIRECTING" ? <p className="signin-copy">Taking you to sign in…</p> : null}

          <p className="signin-issuer">{OIDC_ISSUER}</p>
        </section>
      </div>
    </main>
  );
}
